import React, { useState } from 'react';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import { Icon } from 'legacy-packages/ft-cb';
import cn from 'classnames';

import AnchorLink from 'react-anchor-link-smooth-scroll';
import DisplayLessons from './DisplayLessons';
import {
  progressTypes,
  determineResultProgress,
} from './utility/progressCalculator';
import Bars from '../Bars/Bars';

import getStudentDescription from '../utility/studentFocusDescription';
import getQuestionData from '../utility/questionData';

const readableAreas = {};
readableAreas[progressTypes.OPPORTUNITY_FOR_GROWTH] = 'Opportunity for Growth';
readableAreas[progressTypes.PROGRESSING] = 'Progressing';
readableAreas[progressTypes.STRENGTH] = 'Strength';

const resultIconNames = {};
resultIconNames[progressTypes.OPPORTUNITY_FOR_GROWTH] = 'acorn';
resultIconNames[progressTypes.PROGRESSING] = 'on_track';
resultIconNames[progressTypes.STRENGTH] = 'strength';

const DisplayStatus = ({ area, progressCategory, isOpen }) => {
  const questionVal =
    area.status.questionCount === 1 ? 'question' : 'questions';
  return (
    <span className={cn('area-display-status', { selected: isOpen })}>
      <span className="area-status-icon">
        <Icon size="1.8em" name={resultIconNames[progressCategory]} />
      </span>
      <span className="area-display-status-readable">
        <span className="area-display-status-rating">
          {readableAreas[progressCategory]}
        </span>
        <span className="area-display-status-questions">
          based on {area.status.questionCount} {questionVal}
        </span>
      </span>
    </span>
  );
};

const studentCorrectLine = (correct, answered) => {
  let icon;
  let text;

  if (!correct) {
    icon = (
      <Icon name="incorrect" className="student-incorrect-icon student-icon" />
    );
    if (answered) {
      text = 'Incorrect';
    } else {
      text = 'Unanswered';
    }
  } else {
    icon = (
      <Icon
        name="correct"
        className="student-correct-icon student-icon"
        size="1.3em"
      />
    );

    text = 'Correct';
  }

  return (
    <span className="student-correct-label">
      {icon} <span>{text}</span>
    </span>
  );
};

const StudentScore = ({ score, title, link, router }) => {
  const answered = score && score.score !== null;
  const correct = answered ? score.score === score.maxScore : false;
  return (
    <div className="bar-row">
      <span className="bar-row-label">
        <AnchorLink href="#question-table" onClick={() => router.push(link)}>
          {title}
        </AnchorLink>
      </span>
      <span className="bar-row-label">
        {studentCorrectLine(correct, answered)}
      </span>
    </div>
  );
};

const StudentScoresByArea = ({ items, router }) => (
  <section className={cn('bar-container', 'no-label')}>
    <div>
      {items.map((item) => (
        <StudentScore {...item} router={router} key={item.key || item.title} />
      ))}
    </div>
  </section>
);

const AreaForGrowthDisplay = ({
  area,
  borderBottom = true,
  isAdminOrCoordinator,
  isStudent,
  isTeacher,
  router,
  studentId,
  studentView,
  subjectId,
  useClassroomResourceAddress,
  showLearningCheckpointEnhancedTab,
}) => {
  const progressCategory = determineResultProgress(
    area.average_percentage,
    subjectId,
  );
  const [popupIsOpen, setPopupIsOpen] = useState(false);

  const focusLinks = area.focusLinks || [];
  const previous = focusLinks.filter(
    (lesson) => lesson.contentType.toUpperCase() === 'PREVIOUS',
  );
  const upcoming = focusLinks.filter(
    (lesson) => lesson.contentType.toUpperCase() === 'UPCOMING',
  );
  const questionsData = area.items.map((item) =>
    getQuestionData(
      item,
      subjectId,
      isAdminOrCoordinator,
      isTeacher,
      isStudent,
      studentView,
      studentId,
      showLearningCheckpointEnhancedTab,
    ),
  );

  return (
    <div className={cn('area-display', { 'bordered-bottom': borderBottom })}>
      <span className="area-display-title">
        {area.displayName}
        {studentView && (
          <span className="area-display-description">
            {getStudentDescription(subjectId, area.name)}
          </span>
        )}
      </span>
      <span className={cn('area-status-container', { student: studentView })}>
        <PopOver
          trigger={
            <DisplayStatus
              area={area}
              progressCategory={progressCategory}
              isOpen={popupIsOpen}
            />
          }
          size="wide"
          onOpen={() => setPopupIsOpen(true)}
          onClose={() => setPopupIsOpen(false)}
          showCloseButton
        >
          {!studentView ? (
            <Bars
              barsData={questionsData}
              percentStyleClass="blue-background"
            />
          ) : (
            <StudentScoresByArea items={questionsData} router={router} />
          )}
        </PopOver>
        {((previous && previous.length > 0) ||
          (upcoming && upcoming.length > 0)) &&
          isTeacher &&
          !studentView && (
            <span className="area-status-links">
              {previous && previous.length > 0 && (
                <DisplayLessons
                  key={`${area.name}-previous`}
                  label="Reflect on Previous Lessons"
                  lessons={previous}
                  subjectId={subjectId}
                  useClassroomResourceAddress={useClassroomResourceAddress}
                />
              )}
              {upcoming && upcoming.length > 0 && (
                <DisplayLessons
                  key={`${area.name}-upcoming`}
                  label="Plan for Upcoming Lessons"
                  lessons={upcoming}
                  subjectId={subjectId}
                  useClassroomResourceAddress={useClassroomResourceAddress}
                />
              )}
            </span>
          )}
      </span>
    </div>
  );
};

export default AreaForGrowthDisplay;
